import { TextField } from "@mui/material";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState } from "react";

interface ILocationAutocompleteProps {
  onPlaceSelected: (place: google.maps.places.PlaceResult | null) => void;
  label: string;
  placeholder: string;
  value: string;
  mapId?: string;
}

function LocationAutocomplete({
  onPlaceSelected,
  label,
  placeholder,
  value,
  mapId = "my-calendar-map",
}: ILocationAutocompleteProps) {
  const mapInstance = useMap(mapId);
  const placesLibrary = useMapsLibrary("places");
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [inputValue, setInputValue] = useState<string>(value || "");

  useEffect(() => {
    setInputValue(value); // Keep local input state in sync when value changes from outside
  }, [value]);

  useEffect(() => {
    if (!mapInstance || !inputRef.current || !placesLibrary) return;
    // Initialize Google Places Autocomplete
    const options = {
      fields: ["geometry", "name", "formatted_address", "place_id"],
    };
    autocompleteRef.current = new placesLibrary.Autocomplete(
      inputRef.current,
      options,
    );
    // Add place_changed event listener
    const listener = autocompleteRef.current.addListener(
      "place_changed",
      () => {
        const place: google.maps.places.PlaceResult =
          autocompleteRef.current.getPlace();
        if (place && place.geometry) {
          onPlaceSelected(place);
          setInputValue(place.formatted_address || ""); // Update input field with selected address
        }
      },
    );

    // Cleanup
    return () => {
      if (listener) google.maps.event.removeListener(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInstance, onPlaceSelected, placesLibrary]);

  return (
    <TextField
      inputRef={inputRef}
      label={label}
      placeholder={placeholder}
      value={inputValue}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        console.log("e.target.value ==>", e.target.value);
        setInputValue(e.target.value);
        if (!e.target.value) onPlaceSelected(null);
      }}
      variant="outlined"
      fullWidth
      margin="normal"
      size="small"
      slotProps={{
        inputLabel: { shrink: !!inputValue },
      }}
    />
  );
}

export default LocationAutocomplete;
